"use client";
import {
  ArrowRight,
  ContactChat,
  ContactMail,
  ContactPhone,
} from "@/components/bnp-icons";
import { Button, FormInput, Hero, Nav } from "@/components/bnp-ui";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import { TextInput, Textarea } from "@mantine/core";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { ToastContainer, toast } from "react-toastify";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { UserService } from "@/services";
import parseHttpError from "@/utils/parseHttpError";
import FooterAd from "@/components/shared/ads/FooterAd";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage, AdPlacement } from "@/types/advertising";
import { ContentSidebar } from "@/components/layouts/Others/ContentSidebar";

const defaultValues = {
  name: "",
  email: "",
  phone: "",
  city: "",
  message: "",
  recaptcha: "",
};

const validationSchema = yup.object().shape({
  name: yup.string().required("Enter your full name"),
  email: yup.string().email("Enter a valid email").required("Enter your email"),
  phone: yup
    .string()
    .matches(/^[0-9+\s-()]+$/, "Enter a valid phone number")
    .required("Enter your phone number"),
  city: yup.string().required("Enter your city"),
  message: yup.string().required("Please enter your message"),
  recaptcha: yup
    .string()
    .required("Please complete the reCAPTCHA verification"),
});

const Contact = () => {
  const RecaptchaComponent = ReCAPTCHA as unknown as React.FC<any>;
  const recaptchaRef: any = useRef(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const {
    getValues,
    setValue,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const handleRecaptchaChange = (value: any) => {
    setValue("recaptcha", value);
    setRecaptchaError("");
  };

  const submitContactForm = async () => {
    try {
      const response = await UserService.contactUs(getValues());
      return response?.data;
    } catch (error: any) {
      console.error(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const mutation = useMutation({
    mutationFn: submitContactForm,
    onSuccess: (response) => {
      toast.success(response.message || "Message sent successfully!");
      reset(defaultValues);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send message. Please try again.");
    },
  });

  const onSubmit = () => mutation.mutate();

  // NOTE: The backend only serves ads on the homepage for now. We fetch the
  // homepage ad set here so the placements render while testing; swap back to
  // the real page once those ads are available.
  const { footerAds, headerAds, popupAds, sidebarAds } = usePageAds(
    AdPage.HOME,
  );

  return (
    <HomeWrapper
      minContentHeight={"150vh"}
      footerSlot={footerAds[0] ? <FooterAd ad={footerAds[0]} /> : undefined}
    >
      {headerAds[0] && (
        <div className="w-full max-w-[1480px] mx-auto px-4 pt-4 pb-2">
          <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
        </div>
      )}

      <ContentSidebar
        sidebarContent={
          sidebarAds?.[0] && (
            <div className="w-full mx-auto px-4 pt-4 pb-2">
              <AdRenderer ad={sidebarAds[0]} placement={AdPlacement.SIDEBAR} />
            </div>
          )
        }
        topOffset={200}
      >
        <>
          <div className="relative flex flex-col items-center">
            <div className="flex flex-col md:flex-row w-[380px] h-[844px] absolute mt-10 top-[90%] md:top-[100%] self-center bg-transparent rounded-xl md:h-[225px] md:w-[925px] gap-8">
              <div className=" w-[319px] h-[260px] flex flex-col gap-7  text-blue-primary justify-center items-center self-center rounded-xl lg:h-[260px] lg:w-[319px] bg-white border border-grey-3">
                <ContactMail />
                <p className="font-bold text-lg">Send us an email</p>
                <small className="text-bnp-cyan font-medium text-sm">
                  info@findnigeriaproperties.com
                </small>
              </div>
              <div className=" w-[319px]  h-[260px] flex  flex-col gap-7  text-blue-primary justify-center items-center  self-center rounded-xl lg:h-[260px] lg:w-[319px] bg-white border border-grey-3">
                <ContactPhone />
                <p className="font-bold text-lg">Give us a call</p>
                <div className="flex items-center gap-2 ">
                  <small className="text-bnp-cyan font-medium text-sm">
                    0805 337 6942
                  </small>
                </div>
              </div>
              <div className=" w-[319px]  h-[260px] flex flex-col gap-7  text-blue-primary justify-center items-center  self-center rounded-xl lg:h-[260px] lg:w-[319px] bg-white border border-grey-3">
                <ContactChat />
                <p className="font-bold text-lg">Chat with us</p>

                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => {
                    const phoneNumber = "08053376942";
                    const formattedNumber = "234" + phoneNumber.substring(1);
                    // const propertyUrl = window.location.href;
                    // const message = encodeURIComponent(
                    //   `Hello, I would like to inquire about a property on FindNigeriaProperties.com.`
                    // );
                    const whatsappUrl = `https://wa.me/${formattedNumber}`;
                    window.open(whatsappUrl, "_blank");
                  }}
                >
                  <small className="text-bnp-cyan font-medium text-sm">
                    Chat now
                  </small>
                  <ArrowRight />
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:mt-[310px] mt-[940px] items-center">
            <div className="flex flex-col gap-5 w-[100%] lg:w-[458px] justify-center items-center">
              <h1 className="text-blue-primary font-extrabold text-3xl text-center">
                Want to reach out directly?
              </h1>
              <p className="text-base font-500 text-blue-primary text-center">
                Get in touch with us today and let&apos;s start a conversation.
                We&apos;re here to listen and respond.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 w-[100%] lg:w-[1104px] mt-8  mb-[150px]">
              <div className="w-[100%] lg:w-[457.8px] rounded-xl">
                <img
                  src={"/assets/images/contact-page-image.png"}
                  alt={"contact-page-image"}
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              <div className="flex justify-center w-[100%] shadow-md lg:w-[597.8px] h-fit lg:h-[660px] bg-white border border-grey-3 rounded-xl py-[53px] lg:px-10">
                <form
                  className="w-full"
                  action="submit"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6 mb-8">
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <div className="w-full lg:w-[250px]">
                          <TextInput
                            type="text"
                            name="name"
                            label="Name"
                            value={value}
                            withAsterisk
                            placeholder="Full name"
                            size="md"
                            radius="md"
                            className="w-full h-[52px]"
                            onChange={onChange}
                            onBlur={onBlur}
                            error={errors.name?.message}
                          />
                        </div>
                      )}
                    />
                    <Controller
                      name="email"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <div className="w-full lg:w-[250px]">
                          <TextInput
                            type="email"
                            name="email"
                            label="Email"
                            value={value}
                            withAsterisk
                            placeholder="example@email.com"
                            size="md"
                            radius="md"
                            className="w-full h-[52px]"
                            onChange={onChange}
                            onBlur={onBlur}
                            error={errors.email?.message}
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6 mb-8">
                    <Controller
                      name="phone"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <div className="w-full lg:w-[250px]">
                          <TextInput
                            type="text"
                            name="phone"
                            label="Phone"
                            value={value}
                            withAsterisk
                            placeholder="ex. 09012345678"
                            size="md"
                            radius="md"
                            className="w-full h-[52px]"
                            onChange={onChange}
                            onBlur={onBlur}
                            error={errors.phone?.message}
                          />
                        </div>
                      )}
                    />
                    <Controller
                      name="city"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <div className="w-full lg:w-[250px]">
                          <TextInput
                            type="text"
                            name="city"
                            label="City"
                            value={value}
                            withAsterisk
                            placeholder="ex. Lagos"
                            size="md"
                            radius="md"
                            className="w-full h-[52px]"
                            onChange={onChange}
                            onBlur={onBlur}
                            error={errors.city?.message}
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="mb-8">
                    <Controller
                      name="message"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Textarea
                          name="message"
                          label="Leave us a message"
                          value={value}
                          withAsterisk
                          placeholder="Message"
                          size="md"
                          radius="md"
                          className="w-full"
                          minRows={4}
                          onChange={onChange}
                          onBlur={onBlur}
                          error={errors.message?.message}
                        />
                      )}
                    />
                  </div>

                  <div className="flex flex-col items-center gap-2 mb-8">
                    <RecaptchaComponent
                      ref={recaptchaRef}
                      sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ""}
                      onChange={handleRecaptchaChange}
                    />
                    {(recaptchaError || errors.recaptcha) && (
                      <span className="text-red-500 text-xs">
                        {recaptchaError || errors.recaptcha?.message}
                      </span>
                    )}
                  </div>

                  <Button
                    className="bg-bnp-cyan text-white text-sm font-extrabold w-[159px] h-[56px] rounded-lg"
                    isLoading={mutation.isPending}
                    onClick={handleSubmit(onSubmit)}
                  >
                    Send Message
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </>
      </ContentSidebar>

      {popupAds[0] && (
        <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
      )}

      {footerAds[0] && (
        <div className="w-full max-w-[1480px] mx-auto px-4 pt-2 pb-12">
          <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
        </div>
      )}
    </HomeWrapper>
  );
};

export default Contact;
